/**
 * Reusable payer search dropdown.
 *
 * Loads popular payers on mount (empty query → cached popular list from Stedi).
 * Typing 2+ characters triggers a debounced Stedi payer directory search.
 * Used by EligibilityForm (initial check) and MedicalSlideout (rerun correction).
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { searchPayers, type PayerOption } from '../services/payers'
import { filterHardcodedPayers, HARDCODED_PAYERS } from '../constants/hardcodedPayers'
import { buildPayerSearchPayload } from '../utils/payerSearchPayload'
import { sortPayersByUsage, boldedPayerCount } from '../utils/sortPayersByUsage'

export type { PayerOption }

export interface SelectedPayer {
  name: string
  tradingPartnerServiceId: string
  stediId: string | null
}

interface PayerSearchProps {
  /** Currently selected payer name (displayed in the input). */
  value: string
  /** Called when the user selects a payer from the dropdown. */
  onSelect: (payer: SelectedPayer) => void
  /** Called when the user clears the selection by typing. */
  onClear?: () => void
  /** HTML id for the input element (for label association). */
  inputId?: string
  /** Placeholder text. */
  placeholder?: string
  /** Disable the input. */
  disabled?: boolean
  /** Override cloud function payer search (used in demo mode). */
  searchFn?: (input?: { query?: string; clinicId?: string }) => Promise<{ payers: PayerOption[] }>
  /** When `value` is a loose display name on mount (e.g. from a cockpit
   *  appointment row), search the directory and auto-select an exact name
   *  match so the parent gets a valid `tradingPartnerServiceId`. Callers
   *  that already hold a valid selection (e.g. rerun edit) should leave
   *  this off to avoid overriding the persisted IDs. */
  autoResolveOnMount?: boolean
  /** When set, results carry `usageCount` for this clinic and are sorted
   *  most-used-first. Omit for callers that don't need clinic-aware ordering
   *  (batch flows, slideout). */
  clinicId?: string
  /** Externally-controlled error state: when set, the input renders invalid
   *  with this message until the user picks from the dropdown. Used by the
   *  slideout when a stored check predates payer-identity persistence (no
   *  stediId) — re-running it as-is would skip payer-specific routing
   *  (e.g. Original Medicare). Internal resolve errors take precedence. */
  invalidMessage?: string | null
  /** Suppress the built-in "Insurance" label — for hosts that already label
   *  the row themselves. The check editor labels it "Payer"; two labels
   *  stacked on one field read as two fields (#318 review). */
  hideLabel?: boolean
  /** Appended to the input's class so a host can match its sibling fields —
   *  the check editor's other inputs are white `check-edit__input`, and the
   *  default `form-field__input` grey read as a locked field next to them. */
  inputClassName?: string
  /**
   * Focus the input on mount (React's `autoFocus`: focus when the element
   * mounts, not the page-load attribute). For a host that mounts this control
   * on activation — the Extraction pane's Payer row (#349) — the opener that
   * held focus has just unmounted, and without this the keyboard user is
   * stranded on `<body>`: Tab lands elsewhere on the page and Escape reaches
   * the drawer's document listener instead of the row.
   */
  autoFocus?: boolean
  /**
   * Render the popup for a DARK host (#349 — the full-screen card viewer).
   *
   * `inputClassName` can only reach the input; the results list, its loading and
   * empty states and the error line are all rendered inside this component, so a
   * dark host cannot restyle them from the outside without reaching in with a
   * descendant selector. On the viewer's near-black panel the default white list
   * arrives as a slab of light over the card being read.
   */
  dark?: boolean
}

const DEBOUNCE_MS = 300
/** Stedi payer directory only returns useful matches from 3+ chars. */
const STEDI_MIN_CHARS = 3

export default function PayerSearch({
  value,
  onSelect,
  onClear,
  inputId = 'payer-search',
  placeholder = 'Search insurance...',
  disabled = false,
  searchFn,
  autoResolveOnMount = false,
  clinicId,
  invalidMessage = null,
  hideLabel = false,
  inputClassName,
  autoFocus = false,
  dark = false,
}: PayerSearchProps) {
  const [query, setQuery] = useState(value)
  const [popularPayers, setPopularPayers] = useState<PayerOption[]>([])
  const [popularError, setPopularError] = useState(false)
  const [searchResults, setSearchResults] = useState<PayerOption[]>([])
  // The last typed search failed — the list must say so, not "No results
  // found", which tells the user no such payer exists.
  const [searchFailed, setSearchFailed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  // Flags when auto-resolve couldn't turn the prefilled name into a real
  // selection (either no exact match, or the directory lookup failed).
  // Cleared as soon as the user picks from the dropdown or edits the input.
  const [resolveError, setResolveError] = useState<'no_match' | 'search_failed' | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const requestIdRef = useRef(0)
  // Last query actually sent to the directory. Lets focus trigger a search
  // for a prefilled value (e.g. slideout edit mode seeds the existing payer
  // name) without re-firing on every focus — without this, clicking into a
  // prefilled input showed "No results found" because only typing searched.
  const lastSearchedRef = useRef<string | null>(null)
  const queryRef = useRef(query)
  queryRef.current = query
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  // Sync external value changes (e.g. parent swapping the selection after a
  // rerun). Skip empty updates: typing in the input fires `onClear` which
  // sets the parent's name to '', and that empty value would otherwise flow
  // back in here and wipe the user's in-progress input.
  useEffect(() => { if (value) setQuery(value) }, [value])

  const doSearch = useCallback(
    async (input?: { query?: string }) => {
      const payload = buildPayerSearchPayload(input, clinicId)
      if (searchFn) return searchFn(payload)
      return searchPayers(payload)
    },
    [searchFn, clinicId],
  )

  // Load popular payers on mount
  useEffect(() => {
    doSearch({}).then(data => setPopularPayers(data.payers)).catch(() => setPopularError(true))
  }, [doSearch])

  // Resolve a prefilled display name into a real selection. Hardcoded RPA
  // payers (VSP, EyeMed) match synchronously; everything else hits Stedi.
  // Runs only on mount, and only when the caller opted in via
  // `autoResolveOnMount` — MedicalSlideout's edit mode passes the existing
  // selection through `value`, and we must not override its persisted IDs.
  useEffect(() => {
    if (!autoResolveOnMount) return
    const prefilled = value.trim()
    if (!prefilled) return

    const hardcoded = HARDCODED_PAYERS.find(
      p => p.name.toLowerCase() === prefilled.toLowerCase(),
    )
    if (hardcoded) {
      onSelectRef.current({
        name: hardcoded.name,
        tradingPartnerServiceId: hardcoded.tradingPartnerServiceId,
        stediId: hardcoded.stediId,
      })
      return
    }

    if (prefilled.length < STEDI_MIN_CHARS) {
      setResolveError('no_match')
      return
    }

    let cancelled = false
    doSearch({ query: prefilled }).then(data => {
      if (cancelled) return
      lastSearchedRef.current = prefilled
      setSearchResults(data.payers)
      if (queryRef.current !== prefilled) return
      const exact = data.payers.find(
        p => p.name.toLowerCase() === prefilled.toLowerCase(),
      )
      if (exact) {
        onSelectRef.current({
          name: exact.name,
          tradingPartnerServiceId: exact.tradingPartnerServiceId,
          stediId: exact.stediId,
        })
      } else {
        // Fuzzy matches are already cached in searchResults — focusing the
        // input reveals them via the normal dropdown path.
        setResolveError('no_match')
      }
    }).catch(() => {
      if (!cancelled) setResolveError('search_failed')
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolves the prefilled name on mount only; a later value change must not override the persisted selection
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Clear debounce on unmount
  useEffect(() => () => clearTimeout(debounceRef.current), [])

  const runSearch = useCallback(async (q: string) => {
    if (q.trim().length < STEDI_MIN_CHARS) {
      setSearchResults([])
      setSearchFailed(false)
      return
    }
    lastSearchedRef.current = q.trim()
    const id = ++requestIdRef.current
    setLoading(true)
    try {
      const data = await doSearch({ query: q.trim() })
      if (requestIdRef.current === id) {
        setSearchResults(data.payers)
        setSearchFailed(false)
      }
    } catch {
      if (requestIdRef.current === id) {
        setSearchResults([])
        setSearchFailed(true)
        // Not searched after all: focusing the box again retries it.
        lastSearchedRef.current = null
      }
    } finally {
      if (requestIdRef.current === id) setLoading(false)
    }
  }, [doSearch])

  function handleInput(val: string) {
    setQuery(val)
    setShowDropdown(true)
    setResolveError(null)
    onClear?.()
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => runSearch(val), DEBOUNCE_MS)
  }

  function handleFocus() {
    setShowDropdown(true)
    // A prefilled value (slideout edit / cockpit prefill) has never been
    // searched — fire it now so the dropdown opens with matches instead of
    // "No results found". Skipped when this query was already searched.
    const q = query.trim()
    if (q.length >= STEDI_MIN_CHARS && lastSearchedRef.current !== q) {
      runSearch(q)
    }
  }

  /**
   * Leaving the box without picking puts the host's value back (#349 review).
   *
   * For a host whose value moves only on a pick (the card rows, the viewer's
   * panel), typing is exploration: a box left showing "cig" over a draft that
   * still held "Aetna" showed one payer while Save would send another. Only
   * when the host holds a value — the hosts that blank theirs on every
   * keystroke (the manual form, the check editor) keep the typed text, as they
   * always did. Focus moving to one of this control's own result buttons
   * (keyboard Tab) is not leaving.
   */
  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    const next = e.relatedTarget as Node | null
    if (next && containerRef.current?.contains(next)) return
    if (value && query !== value) setQuery(value)
  }

  function handleSelect(payer: PayerOption) {
    setQuery(payer.name)
    setResolveError(null)
    onSelect({
      name: payer.name,
      tradingPartnerServiceId: payer.tradingPartnerServiceId,
      stediId: payer.stediId,
    })
    setShowDropdown(false)
  }

  const trimmedQuery = query.trim()
  const hasQuery = trimmedQuery.length > 0
  const isSearching = trimmedQuery.length >= STEDI_MIN_CHARS

  // Merge hardcoded payers (VSP and future additions) with the live results,
  // then sort by per-clinic usage:
  //   - Items with `usageCount > 0` come first, ranked by count desc.
  //   - Items with no usage keep Stedi's returned order (relevance ranking
  //     from the directory). Hardcoded entries appear at the head of the
  //     "no usage" group when nothing else is ranked.
  // Empty query → popular payers (no hardcoded prepend); query below Stedi
  // threshold → hardcoded matches only.
  const options = useMemo<PayerOption[]>(() => {
    const base: PayerOption[] = !hasQuery
      ? popularPayers
      : isSearching
        ? searchResults
        : []
    const hardcoded = filterHardcodedPayers(trimmedQuery)
    const seen = new Set(hardcoded.map(h => h.tradingPartnerServiceId))
    const merged = [...hardcoded, ...base.filter(p => !seen.has(p.tradingPartnerServiceId))]
    return sortPayersByUsage(merged)
  }, [hasQuery, isSearching, trimmedQuery, searchResults, popularPayers])

  const boldedCount = useMemo(() => boldedPayerCount(options), [options])

  return (
    <div className="form-field" ref={containerRef}>
      {!hideLabel && <label className="form-field__label" htmlFor={inputId}>Insurance</label>}
      <input
        id={inputId}
        className={`form-field__input${(resolveError || invalidMessage) ? ' is-invalid' : ''}${inputClassName ? ` ${inputClassName}` : ''}`}
        value={query}
        onChange={e => handleInput(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoComplete="off"
        autoFocus={autoFocus}
        disabled={disabled}
      />
      {resolveError ? (
        <div className={`form-field__error${dark ? ' form-field__error--dark' : ''}`}>
          {resolveError === 'no_match'
            ? "This payer isn't in our directory — please select one from the list."
            : 'Could not look up the payer. Please select one from the list.'}
        </div>
      ) : invalidMessage ? (
        <div className={`form-field__error${dark ? ' form-field__error--dark' : ''}`}>{invalidMessage}</div>
      ) : null}
      {showDropdown && (loading || options.length > 0 || hasQuery || (!hasQuery && popularError)) && (
        <div className={`payer-dropdown${dark ? ' payer-dropdown--dark' : ''}`}>
          {loading && <div className="payer-dropdown__loading">Searching...</div>}
          {options.map((p, i) => (
            <button
              key={p.tradingPartnerServiceId}
              type="button"
              className="payer-dropdown__item"
              // A click on a result must not move focus out of the input. A
              // host that closes its editor when focus leaves it (the
              // Extraction pane's Payer row, #349) would otherwise tear this
              // list down before the click lands — Safari and Firefox on
              // macOS never focus a clicked button, so the input's blur
              // arrives with no `relatedTarget` at all. Keeping focus in the
              // input also stops it silently dropping to <body> in Chrome
              // once the picked button unmounts with the list.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(p)}
            >
              {p.logoUrl && <img src={p.logoUrl} alt="" className="payer-dropdown__logo" />}
              <span style={i < boldedCount ? { fontWeight: 700 } : undefined}>
                {p.name}
              </span>
            </button>
          ))}
          {!loading && options.length === 0 && hasQuery && !isSearching && (
            <div className="payer-dropdown__empty">
              Type {STEDI_MIN_CHARS} or more characters to search insurances.
            </div>
          )}
          {!loading && isSearching && searchFailed && (
            <div className="payer-dropdown__empty">Payer search is temporarily unavailable. Try again.</div>
          )}
          {!loading && options.length === 0 && isSearching && !searchFailed && (
            <div className="payer-dropdown__empty">No results found</div>
          )}
          {!hasQuery && popularError && !loading && (
            <div className="payer-dropdown__empty">Could not load popular payers. Type to search.</div>
          )}
        </div>
      )}
    </div>
  )
}
